import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Grid,
    Card,
    CardContent,
    CardMedia,
    Button,
    Stack,
    styled,
    useTheme,
    useMediaQuery,
    TextField,
    InputAdornment,
    Fab,
    Zoom,
    LinearProgress,
} from "@mui/material";

/* Icons */
import CodeIcon from "@mui/icons-material/Code";
import { AiOutlineWifi } from "react-icons/ai";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

/* Custom Components */
import RadioOption from "../../components/RadioOption";
import Typography from "../../components/Typography";
import LanguageTabs from "../../components/LanguageTabs";
import LectureTimer from "../../components/LectureTimer";

/* Premium Option‑A Background */
const bgImage = "https://images.unsplash.com/photo-1499951360447-b19be8fe80f5";

/* Styled Components - Clean Code Principles */

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.90), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingTop: theme.spacing(15),
    paddingBottom: theme.spacing(10),
    color: "white",
    [theme.breakpoints.down("sm")]: {
        paddingTop: theme.spacing(12),
        paddingBottom: theme.spacing(5),
    },
}));

// Professional glass card for a "Smart Hub" tech feel
const DashboardGlassCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.1)",
    height: "100%",
}));

const TopicBox = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    backgroundColor: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: "10px",
    padding: theme.spacing(1.5, 2),
    transition: "0.3s",
    "&:hover": {
        backgroundColor: "rgba(218, 165, 32, 0.1)",
        borderColor: "goldenrod",
    },
}));

const ViewButton = styled(Button)(({ theme }) => ({
    backgroundColor: "goldenrod",
    color: "#100720",
    fontWeight: 700,
    borderRadius: "999px",
    padding: "6px 20px",
    textTransform: "none",
    "&:hover": {
        backgroundColor: "#e0b200",
        transform: "scale(1.05)",
    },
}));

export default function IoTDashboard() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    /* State Management */
    const [searchQuery, setSearchQuery] = useState("");
    const [showScroll, setShowScroll] = useState(false);
    const [completedCount, setCompletedCount] = useState(0);

    /* IoT TOPICS */
    const iotTopics = [
        { title: "Introduction to IoT", link: "/iot-introduction" },
        { title: "Embedded Systems & Microcontrollers", link: "/iot-embedded-systems" },
        { title: "Embedded Systems Tutorials", link: "/embedded-system-tutorials" },
        { title: "Sensors & Actuators", link: "/iot-sensors-actuators" },
        { title: "Wireless Communication", link: "/iot-wireless-communication" },
        { title: "IoT Protocols", link: "/iot-protocols" },
        { title: "Networking & Edge Devices", link: "/iot-networking-edge" },
        { title: "Cloud Integration & APIs", link: "/iot-cloud-apis" },
        { title: "Security in IoT", link: "/iot-security" },
        { title: "IoT with Python & Node.js", link: "/iot-python-node" },
        { title: "IoT with CPP & Node.js", link: "/iot-cpp-node" },
        { title: "Building Smart Devices", link: "/iot-smart-devices" },
        { title: "Project: Home Automation", link: "/iot-home-automation" },
        { title: "SmartLearnIoT: Programing Lab", link: "/embedded-c-programming" },
        { title: "SmartLearnIoT: IoT Fullstack Project", link: "/smart-learn-fullstack-project" },
    ];

    /* Logic: Search Filtering & Progress Tracker */
    const filteredTopics = useMemo(() => {
        return iotTopics.filter(topic =>
            topic.title.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [searchQuery]);

    const progressPercentage = (completedCount / iotTopics.length) * 100;

    useEffect(() => {
        const checkScroll = () => setShowScroll(window.pageYOffset > 400);
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    const handleScrollTop = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    return (
        <PageWrapper>
            {/* ---------------- GOLDEN BANNER ---------------- */}
            <Box sx={{ textAlign: "center", mb: 4, mt: 16, px: 2 }}>
                <Typography
                    variant={isMobile ? "h6" : "h3"}
                    sx={{
                        fontWeight: 800,
                        color: "goldenrod",
                        px: { xs: 3, md: 6 },
                        py: 1,
                        borderRadius: "999px",
                        display: "inline-block",
                        boxShadow: "0 6px 20px rgba(218, 165, 32, 0.3)",
                    }}
                >
                    MASTERING IOT MODULES
                </Typography>
            </Box>

            {/* ---------------- TIMER & TABS ---------------- */}
            <LectureTimer />
            <LanguageTabs active="IoT" />

            {/* ---------------- MAIN CONTENT ---------------- */}
            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 }, mt: 2 }}>

                {/* LEFT COLUMN: Hardware & Lab Dashboard */}
                <Grid item xs={12} md={5}>
                    <DashboardGlassCard>
                        <Typography variant="h6" sx={{ fontWeight: 700, textAlign: "center", mb: 2, color: "goldenrod" }}>
                            IOT FUNDAMENTALS
                        </Typography>

                        <Card sx={{ width: "100%", backgroundColor: "#f5f5f5", textAlign: "center", padding: "15px", borderRadius: "12px", mt: 2 }}>
                            <CardMedia
                                component="iframe"
                                height={isMobile ? "200" : "260"}
                                src="https://www.youtube.com/embed/vLnPwxZdW4Y"
                                title="IoT Tutorial"
                                allowFullScreen
                                sx={{ borderRadius: "8px", border: "none", mb: 2 }}
                            />

                            <CardContent sx={{ color: "#100720" }}>
                                <Typography variant="h6" fontWeight={800} gutterBottom>
                                    Build Your First Smart System
                                </Typography>

                                <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ mb: 2 }}>
                                    <AiOutlineWifi style={{ fontSize: "1.2rem" }} />
                                    <Typography variant="body2" fontWeight={600}>
                                        Hardware & Software Integration
                                    </Typography>
                                </Stack>

                                <Link to="/iot-lab" style={{ textDecoration: "none" }}>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        startIcon={<CodeIcon />}
                                        sx={{
                                            bgcolor: "#100720",
                                            color: "white",
                                            fontWeight: "bold",
                                            py: 1.5,
                                            borderRadius: "8px",
                                            "&:hover": { bgcolor: "#1a0c36" }
                                        }}
                                    >
                                        Launch Lab Exercises
                                    </Button>
                                </Link>
                            </CardContent>
                        </Card>
                    </DashboardGlassCard>
                </Grid>

                {/* RIGHT COLUMN: Curriculum with Progress & Search */}
                <Grid item xs={12} md={7}>
                    <DashboardGlassCard>
                        <Typography variant="h6" sx={{ fontWeight: 700,color: "#f5f5f5", textAlign: "center", mb: 3 }}>
                            EXPLORE IOT MODULES
                        </Typography>

                        {/* Progress Tracker */}
                        <Box sx={{ mb: 3, px: 1 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                                <Typography variant="caption" sx={{ color: "goldenrod", fontWeight: "bold" }}>
                                    Device Mastery Progress
                                </Typography>
                                <Typography variant="caption" sx={{ color: "goldenrod", fontWeight: "bold" }}>
                                    {Math.round(progressPercentage)}%
                                </Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={progressPercentage}
                                sx={{
                                    height: 8,
                                    borderRadius: 5,
                                    bgcolor: "rgba(255,255,255,0.1)",
                                    "& .MuiLinearProgress-bar": { bgcolor: "goldenrod" }
                                }}
                            />
                        </Box>

                        {/* Search Component */}
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Find a module (e.g. Sensors, Protocols, Cloud)..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            sx={{ mb: 3 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: "goldenrod" }} />
                                    </InputAdornment>
                                ),
                                sx: {
                                    color: "white",
                                    bgcolor: "rgba(255,255,255,0.05)",
                                    borderRadius: "8px",
                                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(218, 165, 32, 0.3)" }
                                }
                            }}
                        />

                        <Stack spacing={1.5}>
                            {filteredTopics.length > 0 ? (
                                filteredTopics.map((topic, i) => (
                                    <TopicBox key={i}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexGrow: 1 }}>
                                            <RadioOption />
                                            <Typography variant="body2" sx={{ fontWeight: 500,color: "#f5f5f5",}}>
                                                {topic.title}
                                            </Typography>
                                        </Box>
                                        <Link to={topic.link} style={{ textDecoration: "none" }}>
                                            <ViewButton size="small">View</ViewButton>
                                        </Link>
                                    </TopicBox>
                                ))
                            ) : (
                                <Typography sx={{ textAlign: "center", opacity: 0.5, py: 4 }}>
                                    No IoT modules found matching "{searchQuery}"
                                </Typography>
                            )}
                        </Stack>
                    </DashboardGlassCard>
                </Grid>
            </Grid>

            {/* Back to Top FAB */}
            <Zoom in={showScroll}>
                <Fab
                    size="small"
                    onClick={handleScrollTop}
                    sx={{ position: "fixed", bottom: 25, right: 25, bgcolor: "goldenrod", "&:hover": { bgcolor: "#e0b200" } }}
                >
                    <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                </Fab>
            </Zoom>
        </PageWrapper>
    );
}
